// SCR-STAFF-411 (AP-BIL-05؛ US-BIL-120، 121): حساب سازمان‌های طرف قرارداد — مطالبه باز هر قرارداد، صدور صورت‌حساب
// دوره‌ای تجمیعی با فهرست ذی‌نفعان و مبالغ (بدون جزئیات بالینی — AC-BIL-121.1)، و ثبت پرداخت سازمان که به
// صورتحساب‌های همان دوره تخصیص می‌یابد.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type BilOrganizationAccountView,
  type BilOrganizationReceivableView,
  type BilOrganizationStatementView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { BusinessDateField, DataTable, faDigits, FormDialog, ListPage, toJalali, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Amount, SCR_STAFF_411 } from './common';

type Section = 'receivables' | 'statements';

export function OrganizationAccountsPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const tenantId = session?.tenantId;
  const [contractId, setContractId] = useState<string | null>(null);
  const [section, setSection] = useState<Section>('receivables');
  const [issuing, setIssuing] = useState(false);
  const [paying, setPaying] = useState<BilOrganizationStatementView | null>(null);

  const accounts = useQuery({
    queryKey: ['billing', tenantId, 'organization-accounts'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/organization-accounts')),
  });
  const selected = (accounts.data?.items ?? []).find((row) => row.contractId === contractId) ?? accounts.data?.items?.[0] ?? null;

  const receivables = useQuery({
    queryKey: ['billing', tenantId, 'organization-receivables', selected?.contractId ?? 'none'],
    enabled: Boolean(selected) && section === 'receivables',
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/billing/organization-accounts/{contractId}/receivables', {
          params: { path: { contractId: selected!.contractId } },
        }),
      ),
  });

  const statements = useQuery({
    queryKey: ['billing', tenantId, 'organization-statements', selected?.contractId ?? 'none'],
    enabled: Boolean(selected) && section === 'statements',
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/billing/organization-accounts/{contractId}/statements', {
          params: { path: { contractId: selected!.contractId } },
        }),
      ),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['billing', tenantId, 'organization-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['billing', tenantId, 'organization-receivables', selected?.contractId ?? 'none'] });
    void queryClient.invalidateQueries({ queryKey: ['billing', tenantId, 'organization-statements', selected?.contractId ?? 'none'] });
  };

  const accountColumns: DataTableColumn<BilOrganizationAccountView>[] = [
    {
      id: 'organization',
      headerKey: 'bil:organization.name',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span data-testid={`bil-org-name-${row.contractId}`}>{row.organizationName}</span>
          <Typography variant="caption" color="text.secondary">
            <bdi dir="ltr">{row.contractCode}</bdi>
          </Typography>
        </Stack>
      ),
    },
    {
      id: 'open',
      headerKey: 'bil:organization.open',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Amount money={row.openAmount} testId={`bil-org-open-${row.contractId}`} />
          <Typography variant="caption" color="text.secondary">{t('bil:organization.items', { count: row.openCount })}</Typography>
        </Stack>
      ),
    },
    { id: 'statemented', headerKey: 'bil:organization.statemented', cell: (row) => <Amount money={row.statementedAmount} /> },
    { id: 'settled', headerKey: 'bil:organization.settled', cell: (row) => <Amount money={row.settledAmount} /> },
    {
      id: 'period',
      headerKey: 'bil:organization.open_period',
      cell: (row) => (row.firstOpenDate ? `${toJalali(row.firstOpenDate)} — ${toJalali(row.lastOpenDate ?? row.firstOpenDate)}` : '—'),
    },
    {
      id: 'actions',
      headerKey: 'bil:common.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setContractId(row.contractId)} data-testid={`bil-org-select-${row.contractId}`}>
          {t('bil:organization.open_account')}
        </Button>
      ),
    },
  ];

  const receivableColumns: DataTableColumn<BilOrganizationReceivableView>[] = [
    { id: 'patient', headerKey: 'bil:organization.beneficiary', cell: (row) => row.patientName ?? '—' },
    { id: 'invoice', headerKey: 'bil:organization.invoice', cell: (row) => <bdi dir="ltr">{row.invoiceNumber}</bdi> },
    { id: 'date', headerKey: 'bil:organization.service_date', cell: (row) => toJalali(row.businessDate) },
    { id: 'amount', headerKey: 'bil:organization.amount', cell: (row) => <Amount money={row.amount} testId={`bil-org-receivable-${row.id}`} /> },
    { id: 'status', headerKey: 'bil:organization.status', cell: (row) => t(`bil:organization_status.${row.status}`) },
  ];

  const statementColumns: DataTableColumn<BilOrganizationStatementView>[] = [
    { id: 'number', headerKey: 'bil:organization.statement_number', cell: (row) => <bdi dir="ltr">{row.number}</bdi> },
    { id: 'period', headerKey: 'bil:organization.period', cell: (row) => `${toJalali(row.periodFrom)} — ${toJalali(row.periodTo)}` },
    { id: 'items', headerKey: 'bil:organization.items_count', cell: (row) => faDigits(row.itemCount) },
    { id: 'total', headerKey: 'bil:organization.total', cell: (row) => <Amount money={row.total} testId={`bil-org-statement-total-${row.id}`} /> },
    { id: 'balance', headerKey: 'bil:organization.balance', cell: (row) => <Amount money={row.balance} testId={`bil-org-statement-balance-${row.id}`} /> },
    {
      id: 'actions',
      headerKey: 'bil:common.actions',
      cell: (row) =>
        row.balance.amount > 0 ? (
          <Button size="small" variant="outlined" onClick={() => setPaying(row)} data-testid={`bil-org-pay-${row.id}`}>
            {t('bil:organization.record_payment')}
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_411}
      titleKey="bil:organization.title"
      subtitleKey="bil:organization.subtitle"
      actions={
        selected ? (
          <Button variant="contained" onClick={() => setIssuing(true)} data-testid="bil-org-issue-statement">
            {t('bil:organization.issue_statement')}
          </Button>
        ) : undefined
      }
    >
      <Stack spacing={2}>
        <DataTable
          rows={accounts.data?.items}
          columns={accountColumns}
          getRowId={(row) => row.contractId}
          isLoading={accounts.isLoading}
          error={accounts.error}
          onRetry={() => void accounts.refetch()}
          emptyTitleKey="bil:organization.empty"
          captionKey="bil:organization.title"
        />
        {selected ? (
          <Stack spacing={1}>
            <Typography variant="h3" component="h3">{selected.organizationName}</Typography>
            <Tabs value={section} onChange={(_event, next: Section) => setSection(next)} aria-label={t('bil:organization.title')}>
              <Tab value="receivables" label={t('bil:organization.receivables')} data-testid="bil-org-tab-receivables" />
              <Tab value="statements" label={t('bil:organization.statements')} data-testid="bil-org-tab-statements" />
            </Tabs>
            {section === 'receivables' ? (
              <DataTable
                rows={receivables.data?.items}
                columns={receivableColumns}
                getRowId={(row) => row.id}
                isLoading={receivables.isLoading}
                error={receivables.error}
                onRetry={() => void receivables.refetch()}
                emptyTitleKey="bil:organization.receivables_empty"
                captionKey="bil:organization.receivables"
              />
            ) : (
              <Stack spacing={1}>
                <DataTable
                  rows={statements.data?.items}
                  columns={statementColumns}
                  getRowId={(row) => row.id}
                  isLoading={statements.isLoading}
                  error={statements.error}
                  onRetry={() => void statements.refetch()}
                  emptyTitleKey="bil:organization.statements_empty"
                  captionKey="bil:organization.statements"
                />
                {(statements.data?.items ?? []).map((statement) => (
                  <StatementLines key={statement.id} statement={statement} />
                ))}
              </Stack>
            )}
          </Stack>
        ) : null}
      </Stack>
      {issuing && selected ? (
        <IssueStatementDialog
          contractId={selected.contractId}
          onClose={() => setIssuing(false)}
          onIssued={() => {
            setIssuing(false);
            setSection('statements');
            refresh();
          }}
        />
      ) : null}
      {paying ? (
        <PaymentDialog
          statement={paying}
          onClose={() => setPaying(null)}
          onPaid={() => {
            setPaying(null);
            refresh();
          }}
        />
      ) : null}
    </ListPage>
  );
}

/** فهرست ذی‌نفعان، خدمات، تاریخ‌ها و مبالغ یک صورت‌حساب دوره‌ای (AC-BIL-121.1). */
function StatementLines({ statement }: { statement: BilOrganizationStatementView }) {
  const { t } = useTranslation('bil');
  return (
    <Stack spacing={0.5} data-testid={`bil-org-statement-lines-${statement.id}`}>
      <Typography variant="subtitle2">
        {t('bil:organization.statement_lines', { number: statement.number })}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('bil:organization.beneficiary')}</TableCell>
            <TableCell>{t('bil:organization.service')}</TableCell>
            <TableCell>{t('bil:organization.service_date')}</TableCell>
            <TableCell>{t('bil:organization.amount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statement.lines.map((line, index) => (
            <TableRow key={`${statement.id}-${index}`}>
              <TableCell>{line.patientName ?? '—'}</TableCell>
              <TableCell>{line.title}</TableCell>
              <TableCell>{toJalali(line.businessDate)}</TableCell>
              <TableCell><Amount money={line.amount} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

/** صدور صورت‌حساب دوره‌ای برای بازه انتخابی. */
function IssueStatementDialog({ contractId, onClose, onIssued }: { contractId: string; onClose: () => void; onIssued: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const issue = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/organization-accounts/statements', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: { contractId, from, to, branchId: null },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error),
    onSuccess: onIssued,
  });

  return (
    <FormDialog
      open
      titleKey="bil:organization.issue_statement"
      screenId={SCR_STAFF_411}
      onSubmit={() => from && to && issue.mutate()}
      onClose={onClose}
      isDirty={Boolean(from || to)}
      isSubmitting={issue.isPending}
      submitError={issue.error}
      submitKey="bil:organization.issue_statement"
    >
      <Stack spacing={2}>
        <Alert severity="info">{t('bil:organization.issue_hint')}</Alert>
        <BusinessDateField label={t('bil:organization.period_from')} value={from} onChange={setFrom} testId="bil-org-from" />
        <BusinessDateField label={t('bil:organization.period_to')} value={to} onChange={setTo} testId="bil-org-to" />
      </Stack>
    </FormDialog>
  );
}

/** ثبت پرداخت سازمان روی صورت‌حساب دوره‌ای. */
function PaymentDialog({ statement, onClose, onPaid }: { statement: BilOrganizationStatementView; onClose: () => void; onPaid: () => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const [amount, setAmount] = useState(String(statement.balance.amount));
  const [reference, setReference] = useState('');

  const pay = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/organization-accounts/statements/{statementId}:record-payment', {
          params: { path: { statementId: statement.id } },
          headers: { 'Idempotency-Key': idempotency.current() },
          body: { amount: Number(amount), reference: reference || null, receivedOn: null },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error),
    onSuccess: onPaid,
  });

  return (
    <FormDialog
      open
      titleKey="bil:organization.record_payment"
      screenId={SCR_STAFF_411}
      onSubmit={() => Number(amount) > 0 && pay.mutate()}
      onClose={onClose}
      isDirty={amount !== ''}
      isSubmitting={pay.isPending}
      submitError={pay.error}
      submitKey="bil:organization.record_payment"
    >
      <Stack spacing={2}>
        <Typography variant="body2">
          {t('bil:organization.statement_balance', { number: statement.number })} <Amount money={statement.balance} />
        </Typography>
        <TextField
          label={t('bil:organization.amount')}
          value={amount}
          onChange={(event) => setAmount(event.target.value.replace(/[^\d]/gu, ''))}
          slotProps={{ htmlInput: { 'data-testid': 'bil-org-payment-amount', inputMode: 'numeric' } }}
        />
        <TextField
          label={t('bil:organization.reference')}
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'bil-org-payment-reference', maxLength: 64 } }}
        />
      </Stack>
    </FormDialog>
  );
}
